import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';
import subscriptionPlansData from '../data/subscriptionPlans.json';

export type SubscriptionTier = 'basic' | 'plus' | 'pro' | 'care';

export interface SubscriptionPlan {
  id: string;
  tier: SubscriptionTier;
  name: string;
  price: number;
  billingPeriod: 'monthly' | 'yearly';
  features: string[];
  limits: {
    aiResponses: number | 'unlimited';
    avatarMinutes: number | 'unlimited';
    journalEntries: number | 'unlimited';
    voiceJournalEntries: number | 'unlimited';
    voiceFeatures: boolean;
    crisisSupport: boolean;
    therapistMatching: boolean;
    advancedAnalytics: boolean;
    familyAccounts: number;
    prioritySupport: boolean;
    customContent: boolean;
  };
  popular?: boolean;
  savings?: number; // percentage saved on annual
}

export interface UserSubscription {
  tier: SubscriptionTier;
  plan: SubscriptionPlan;
  status: 'active' | 'canceled' | 'past_due' | 'trial' | 'paused';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  trialEnd?: Date;
  cancelAtPeriodEnd: boolean;
  pausedUntil?: Date;
  usage: {
    aiResponses: number;
    avatarMinutes: number;
    journalEntries: number;
    voiceJournalEntries: number;
    monthlyReset: Date;
  };
  premiumTrialsUsed: {
    avatar: boolean;
    audio: boolean;
  };
}

export interface UsageAnalytics {
  featureUsage: Record<string, number>;
  sessionDuration: number[];
  conversionFunnel: {
    visitors: number;
    signups: number;
    trialStarts: number;
    conversions: number;
  };
  churnRisk: 'low' | 'medium' | 'high';
  lifetimeValue: number;
}

interface SubscriptionContextType {
  // Current subscription
  subscription: UserSubscription | null;
  availablePlans: SubscriptionPlan[];
  
  // Subscription management
  upgradePlan: (planId: string) => Promise<void>;
  downgradePlan: (planId: string) => Promise<void>;
  cancelSubscription: () => Promise<void>;
  pauseSubscription: (duration: number) => Promise<void>;
  resumeSubscription: () => Promise<void>;
  
  // Usage tracking
  trackFeatureUsage: (feature: string) => void;
  checkUsageLimit: (feature: string) => boolean;
  getRemainingUsage: (feature: string) => number | 'unlimited';
  
  // Payment & billing
  updatePaymentMethod: () => Promise<void>;
  getBillingHistory: () => Promise<any[]>;
  applyDiscount: (code: string) => Promise<boolean>;
  processPayPalPayment: (orderId: string, amount: number) => Promise<boolean>;
  
  // Analytics
  getUsageAnalytics: () => UsageAnalytics;
  
  // Trial management
  startFreeTrial: (planId: string) => Promise<void>;
  
  isLoading: boolean;
  isProcessingPayment: boolean;
  error: string | null;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

// Import subscription plans from external JSON
const SUBSCRIPTION_PLANS: SubscriptionPlan[] = subscriptionPlansData.plans;

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [subscription, setSubscription] = useState<UserSubscription | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usageAnalytics, setUsageAnalytics] = useState<UsageAnalytics>({
    featureUsage: {},
    sessionDuration: [],
    conversionFunnel: {
      visitors: 1000,
      signups: 150,
      trialStarts: 45,
      conversions: 12
    },
    churnRisk: 'low',
    lifetimeValue: 0
  });

  // Initialize subscription for user
  useEffect(() => {
    if (user) {
      initializeSubscription();
    }
  }, [user]);

  const initializeSubscription = async () => {
    setIsLoading(true);
    try {
      // Load subscription from local storage
      loadSubscriptionFromStorage();
    } catch (error) {
      console.error("Failed to initialize subscription:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadSubscriptionFromStorage = () => {
    // Check for existing subscription in local storage
    const storedSubscription = localStorage.getItem(`subscription_${user?.id}`);
    if (storedSubscription) {
      const parsed = JSON.parse(storedSubscription);
      
      setSubscription({
        ...parsed,
        currentPeriodStart: new Date(parsed.currentPeriodStart),
        currentPeriodEnd: new Date(parsed.currentPeriodEnd),
        trialEnd: parsed.trialEnd ? new Date(parsed.trialEnd) : undefined,
        pausedUntil: parsed.pausedUntil ? new Date(parsed.pausedUntil) : undefined,
        usage: {
          ...parsed.usage,
          monthlyReset: new Date(parsed.usage.monthlyReset)
        },
        // Initialize premiumTrialsUsed if it doesn't exist in stored data
        premiumTrialsUsed: parsed.premiumTrialsUsed || {
          avatar: false,
          audio: false
        }
      });
    } else {
      // Create default basic subscription
      const basicPlan = SUBSCRIPTION_PLANS.find(p => p.tier === 'basic')!;
      const newSubscription: UserSubscription = {
        tier: 'basic',
        plan: basicPlan,
        status: 'active',
        currentPeriodStart: new Date(),
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), // 30 days
        cancelAtPeriodEnd: false,
        usage: {
          aiResponses: 0,
          avatarMinutes: 0,
          journalEntries: 0,
          voiceJournalEntries: 0,
          monthlyReset: new Date()
        },
        premiumTrialsUsed: {
          avatar: false,
          audio: false
        }
      };
      setSubscription(newSubscription);
      saveSubscription(newSubscription);
    }
  };

  const saveSubscription = (sub: UserSubscription) => {
    localStorage.setItem(`subscription_${user?.id}`, JSON.stringify(sub));
    
    // Also save usage data separately for easier access
    localStorage.setItem(`subscription_usage_${user?.id}`, JSON.stringify(sub.usage));
    
    // Save premium trials used
    localStorage.setItem(`premium_trials_${user?.id}`, JSON.stringify(sub.premiumTrialsUsed));
  };

  const saveUsage = (usage: UserSubscription['usage']) => {
    if (!subscription || !user) return;
    
    const updatedSubscription = {
      ...subscription,
      usage
    };
    
    setSubscription(updatedSubscription);
    localStorage.setItem(`subscription_usage_${user.id}`, JSON.stringify(usage));
  };

  const savePremiumTrialsUsed = (premiumTrialsUsed: UserSubscription['premiumTrialsUsed']) => {
    if (!subscription || !user) return;
    
    const updatedSubscription = {
      ...subscription,
      premiumTrialsUsed
    };
    
    setSubscription(updatedSubscription);
    localStorage.setItem(`premium_trials_${user.id}`, JSON.stringify(premiumTrialsUsed));
  };

  const processPayPalPayment = async (orderId: string, amount: number): Promise<boolean> => {
    setIsProcessingPayment(true);
    setError(null);
    
    try {
      console.log(`Processing PayPal payment for order ${orderId} with amount $${amount}`);
      
      // Call Supabase Edge Function to process the payment
      const { data, error } = await supabase.functions.invoke('process-payment', {
        body: {
          orderId,
          amount,
          provider: 'paypal',
          userId: user?.id
        }
      });
      
      if (error) {
        console.error('Payment processing error:', error);
        throw new Error(error.message || 'Payment processing failed');
      }
      
      console.log(`Payment successful for order ${orderId}:`, data);
      
      // Update analytics
      setUsageAnalytics(prev => ({
        ...prev,
        featureUsage: {
          ...prev.featureUsage,
          payment_completed: (prev.featureUsage.payment_completed || 0) + 1
        },
        lifetimeValue: prev.lifetimeValue + amount
      }));
      
      return true;
    } catch (error) {
      console.error("Failed to process PayPal payment:", error);
      setError(error instanceof Error ? error.message : 'Payment processing failed. Please try again.');
      return false;
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const upgradePlan = async (planId: string): Promise<void> => {
    if (!subscription) return;
    
    setIsProcessingPayment(true);
    setError(null);
    
    try {
      const newPlan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
      if (!newPlan) throw new Error('Plan not found');

      // Simulate successful purchase
      console.log(`Successfully purchased plan ${planId}`);
      
      // Update subscription
      const updatedSubscription: UserSubscription = {
        ...subscription,
        tier: newPlan.tier,
        plan: newPlan,
        status: 'active',
        currentPeriodStart: new Date(),
        currentPeriodEnd: new Date(Date.now() + (newPlan.billingPeriod === 'yearly' ? 365 : 30) * 24 * 60 * 60 * 1000),
        cancelAtPeriodEnd: false
      };
      
      setSubscription(updatedSubscription);
      saveSubscription(updatedSubscription);
      
      // Track feature usage
      trackFeatureUsage('subscription_upgrade');
      
    } catch (error) {
      console.error("Failed to upgrade plan:", error);
      setError('Failed to upgrade subscription');
      throw error;
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const downgradePlan = async (planId: string): Promise<void> => {
    if (!subscription) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const newPlan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
      if (!newPlan) throw new Error('Plan not found');

      // Simulate downgrade
      console.log(`Successfully downgraded to plan ${planId}`);
      
      const updatedSubscription: UserSubscription = {
        ...subscription,
        cancelAtPeriodEnd: true
      };

      setSubscription(updatedSubscription);
      saveSubscription(updatedSubscription);
      
    } catch (error) {
      console.error("Failed to downgrade plan:", error);
      setError('Failed to downgrade subscription');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const cancelSubscription = async (): Promise<void> => {
    if (!subscription) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate cancellation
      console.log("Successfully cancelled subscription");
      
      const updatedSubscription: UserSubscription = {
        ...subscription,
        cancelAtPeriodEnd: true
      };

      setSubscription(updatedSubscription);
      saveSubscription(updatedSubscription);
      
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      setError('Failed to cancel subscription');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const pauseSubscription = async (duration: number): Promise<void> => {
    if (!subscription) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate pause
      console.log(`Successfully paused subscription for ${duration} days`);
      
      const pauseUntil = new Date(Date.now() + duration * 24 * 60 * 60 * 1000);
      
      const updatedSubscription: UserSubscription = {
        ...subscription,
        status: 'paused',
        pausedUntil: pauseUntil
      };

      setSubscription(updatedSubscription);
      saveSubscription(updatedSubscription);
      
    } catch (error) {
      console.error("Failed to pause subscription:", error);
      setError('Failed to pause subscription');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resumeSubscription = async (): Promise<void> => {
    if (!subscription) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate resume
      console.log("Successfully resumed subscription");
      
      const updatedSubscription: UserSubscription = {
        ...subscription,
        status: 'active',
        pausedUntil: undefined
      };

      setSubscription(updatedSubscription);
      saveSubscription(updatedSubscription);
      
    } catch (error) {
      console.error("Failed to resume subscription:", error);
      setError('Failed to resume subscription');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const trackFeatureUsage = (feature: string) => {
    if (!subscription || !user) return;

    // Update usage analytics
    setUsageAnalytics(prev => ({
      ...prev,
      featureUsage: {
        ...prev.featureUsage,
        [feature]: (prev.featureUsage[feature] || 0) + 1
      }
    }));

    // Update subscription usage
    if (feature === 'ai_response' && subscription.plan.limits.aiResponses !== 'unlimited') {
      const updatedUsage = {
        ...subscription.usage,
        aiResponses: subscription.usage.aiResponses + 1
      };
      saveUsage(updatedUsage);
    }

    if (feature === 'avatar_session') {
      const updatedUsage = {
        ...subscription.usage,
        avatarMinutes: subscription.usage.avatarMinutes + 1
      };
      saveUsage(updatedUsage);
    }

    if (feature === 'journal_entry') {
      const updatedUsage = {
        ...subscription.usage,
        journalEntries: subscription.usage.journalEntries + 1
      };
      saveUsage(updatedUsage);
    }

    if (feature === 'voice_journal') {
      const updatedUsage = {
        ...subscription.usage,
        voiceJournalEntries: subscription.usage.voiceJournalEntries + 1
      };
      saveUsage(updatedUsage);
    }
  };

  const checkUsageLimit = (feature: string): boolean => {
    // Admin users should have unlimited access to all features
    if (user?.isAdmin) {
      console.log(`Admin user ${user.id} bypassing usage limit check for ${feature}`);
      return true;
    }
    
    if (!subscription || !user) return false;

    // Check if monthly usage needs to be reset
    const now = new Date();
    const lastReset = new Date(subscription.usage.monthlyReset);
    const monthDiff = now.getMonth() - lastReset.getMonth() + 
                     (now.getFullYear() - lastReset.getFullYear()) * 12;
    
    if (monthDiff > 0) {
      // Reset monthly usage
      const updatedUsage = {
        aiResponses: 0,
        avatarMinutes: 0,
        journalEntries: 0,
        voiceJournalEntries: 0,
        monthlyReset: now
      };
      saveUsage(updatedUsage);
    }

    // Crisis support and safety plan are always available to all users
    if (feature === 'crisis_support' || feature === 'safety_plan') {
      return true;
    }

    // Check feature-specific limits
    switch (feature) {
      case 'journal_entry':
        if (subscription.plan.limits.journalEntries === 'unlimited') {
          return true;
        }
        return subscription.usage.journalEntries < subscription.plan.limits.journalEntries;
      
      case 'voice_journal':
        if (subscription.plan.limits.voiceJournalEntries === 'unlimited') {
          return true;
        }
        return subscription.usage.voiceJournalEntries < subscription.plan.limits.voiceJournalEntries;
      
      case 'ai_response':
        if (subscription.plan.limits.aiResponses === 'unlimited') {
          return true;
        }
        return subscription.usage.aiResponses < subscription.plan.limits.aiResponses;
      
      case 'avatar_session':
        // Basic users can't use avatar sessions
        if (subscription.tier === 'basic') {
          return false;
        }
        // Plus users can't use avatar sessions
        if (subscription.tier === 'plus') {
          return false;
        }
        // Pro and Care users have unlimited avatar sessions
        return true;
      
      case 'voice_features':
        // Basic users can use voice journaling but not other voice features
        if (subscription.tier === 'basic' && feature !== 'voice_journal') {
          return false;
        }
        return subscription.plan.limits.voiceFeatures;
      
      case 'advanced_analytics':
        return subscription.plan.limits.advancedAnalytics;
      
      case 'therapist_matching':
        return subscription.plan.limits.therapistMatching;
      
      case 'custom_content':
        return subscription.plan.limits.customContent;
      
      default:
        return true;
    }
  };

  const getRemainingUsage = (feature: string): number | 'unlimited' => {
    // Admin users have unlimited usage for all features
    if (user?.isAdmin) {
      console.log(`Admin user ${user.id} has unlimited usage for ${feature}`);
      return 'unlimited';
    }
    
    if (!subscription) return 0;

    // Crisis support and safety plan are always unlimited for all users
    if (feature === 'crisis_support' || feature === 'safety_plan') {
      return 'unlimited';
    }

    switch (feature) {
      case 'journal_entry':
        if (subscription.plan.limits.journalEntries === 'unlimited') {
          return 'unlimited';
        }
        return Math.max(0, (subscription.plan.limits.journalEntries as number) - subscription.usage.journalEntries);
      
      case 'voice_journal':
        if (subscription.plan.limits.voiceJournalEntries === 'unlimited') {
          return 'unlimited';
        }
        return Math.max(0, (subscription.plan.limits.voiceJournalEntries as number) - subscription.usage.voiceJournalEntries);
      
      case 'ai_response':
        if (subscription.plan.limits.aiResponses === 'unlimited') {
          return 'unlimited';
        }
        return Math.max(0, (subscription.plan.limits.aiResponses as number) - subscription.usage.aiResponses);
      
      case 'avatar_session':
        if (subscription.tier === 'basic' || subscription.tier === 'plus') {
          return 0;
        }
        return 'unlimited';
      
      case 'voice_features':
        if (subscription.tier === 'basic') {
          return 0;
        }
        return 'unlimited';
      
      case 'advanced_analytics':
        if (subscription.tier === 'basic') {
          return 0;
        }
        return 'unlimited';
      
      case 'therapist_matching':
        if (subscription.tier === 'basic' || subscription.tier === 'plus') {
          return 0;
        }
        return 'unlimited';
      
      case 'custom_content':
        if (subscription.tier === 'basic') {
          return 0;
        }
        return 'unlimited';
      
      default:
        return 'unlimited';
    }
  };

  const startFreeTrial = async (planId: string): Promise<void> => {
    if (!subscription) return;
    
    setIsProcessingPayment(true);
    setError(null);
    
    try {
      const plan = SUBSCRIPTION_PLANS.find(p => p.id === planId);
      if (!plan) throw new Error('Plan not found');

      // Simulate trial start
      console.log(`Successfully started trial for plan ${planId}`);
      
      // Set trial end date (7 days from now)
      const trialEnd = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);

      const updatedSubscription: UserSubscription = {
        ...subscription,
        tier: plan.tier,
        plan: plan,
        status: 'trial',
        trialEnd: trialEnd,
        currentPeriodEnd: trialEnd
      };

      setSubscription(updatedSubscription);
      saveSubscription(updatedSubscription);
      
      // Track feature usage
      trackFeatureUsage('trial_start');
      
    } catch (error) {
      console.error("Failed to start free trial:", error);
      setError('Failed to start free trial');
      throw error;
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const updatePaymentMethod = async (): Promise<void> => {
    try {
      // Simulate payment method update
      console.log("Payment method updated successfully");
    } catch (error) {
      console.error("Failed to update payment method:", error);
      setError('Failed to update payment method');
      throw error;
    }
  };

  const getBillingHistory = async (): Promise<any[]> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Return mock data
      return [
        {
          id: 'inv_001',
          date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
          amount: subscription?.plan.price || 0,
          status: 'paid',
          plan: subscription?.plan.name
        }
      ];
    } catch (error) {
      console.error("Failed to get billing history:", error);
      setError('Failed to get billing history');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const applyDiscount = async (code: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Simulate applying discount code
      console.log(`Applying discount code: ${code}`);
      await new Promise(resolve => setTimeout(resolve, 1000));
      return true;
    } catch (error) {
      console.error("Failed to apply discount:", error);
      setError('Failed to apply discount code');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const getUsageAnalytics = (): UsageAnalytics => {
    return usageAnalytics;
  };

  const value = {
    subscription,
    availablePlans: SUBSCRIPTION_PLANS,
    upgradePlan,
    downgradePlan,
    cancelSubscription,
    pauseSubscription,
    resumeSubscription,
    trackFeatureUsage,
    checkUsageLimit,
    getRemainingUsage,
    updatePaymentMethod,
    getBillingHistory,
    applyDiscount,
    processPayPalPayment,
    getUsageAnalytics,
    startFreeTrial,
    isLoading,
    isProcessingPayment,
    error
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};